// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  ActionIcon,
  Tooltip,
  Progress,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconBriefcase,
  IconHistory,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { FilterBar, type FilterConfig } from '../../components/FilterBar';
import { EmptyState } from '../../components/EmptyState';
import {
  useJobs,
  useCreateJob,
  useUpdateJob,
  useDeleteJob,
} from '../../hooks/useJobCosting';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_COLOR_MAP: Record<string, string> = {
  active: 'green',
  on_hold: 'yellow',
  completed: 'blue',
  cancelled: 'gray',
};

interface JobRow extends Record<string, unknown> {
  id: string;
  originalId?: string;
  version?: number;
  isActive?: boolean;
  name: string;
  code: string;
  description?: string;
  clientName?: string;
  status: string;
  budgetAmount?: number;
  actualCost?: number;
  startDate?: string;
  endDate?: string;
  managerId?: string;
  managerName?: string;
  departmentId?: string;
  departmentName?: string;
  createdAt: string;
}

type FormMode = 'create' | 'edit';

export function JobListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const statusFilter = filterValues.status || undefined;

  const { data: jobsRaw, loading: isLoading } = useJobs({
    status: statusFilter,
    page,
    perPage: 25,
  });
  const data = jobsRaw as unknown as JobRow[] | undefined;

  const createMutation = useCreateJob();
  const updateMutation = useUpdateJob();
  const deleteMutation = useDeleteJob();

  const form = useForm({
    initialValues: {
      name: '',
      code: '',
      description: '',
      clientName: '',
      budgetAmount: undefined as number | undefined,
      startDate: '',
      endDate: '',
      managerId: '',
      departmentId: '',
      status: 'ACTIVE',
    },
    validate: {
      name: (v: string) => (v.trim() ? null : 'Job name is required'),
      code: (v: string) => (v.trim() ? null : 'Job code is required'),
      budgetAmount: (v: number | undefined) => {
        if (v !== undefined && v !== null && v < 0) return 'Budget cannot be negative';
        return null;
      },
      endDate: (v: string, values: { startDate: string }) => {
        if (v && values.startDate && v < values.startDate) {
          return 'End date must be after start date';
        }
        return null;
      },
    },
  });

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      options: STATUS_OPTIONS,
      clearable: true,
    },
  ];

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(
      (job) =>
        job.name.toLowerCase().includes(q) ||
        job.code.toLowerCase().includes(q) ||
        (job.clientName && job.clientName.toLowerCase().includes(q)),
    );
  }, [data, search]);

  const openCreate = () => {
    setFormMode('create');
    setEditingId(null);
    form.reset();
    setFormOpen(true);
  };

  const openEdit = (row: JobRow) => {
    setFormMode('edit');
    setEditingId(row.id);
    form.setValues({
      name: row.name,
      code: row.code,
      description: row.description || '',
      clientName: row.clientName || '',
      budgetAmount: row.budgetAmount ?? undefined,
      startDate: row.startDate || '',
      endDate: row.endDate || '',
      managerId: row.managerId || '',
      departmentId: row.departmentId || '',
      status: row.status,
    });
    setFormOpen(true);
  };

  const handleSubmit = form.onSubmit((values) => {
    if (formMode === 'create') {
      const input: Record<string, unknown> = {
        name: values.name.trim(),
        code: values.code.trim(),
      };
      if (values.description.trim()) input.description = values.description.trim();
      if (values.clientName.trim()) input.clientName = values.clientName.trim();
      if (values.budgetAmount !== undefined && values.budgetAmount !== null) {
        input.budgetAmount = values.budgetAmount;
      }
      if (values.startDate) input.startDate = values.startDate;
      if (values.endDate) input.endDate = values.endDate;
      if (values.managerId.trim()) input.managerId = values.managerId.trim();
      if (values.departmentId.trim()) input.departmentId = values.departmentId.trim();

      createMutation.create(input as any)
        .then(() => {
          notifications.show({
            title: 'Job Created',
            message: `"${values.name}" created as v1 (append-only).`,
            color: 'green',
          });
          setFormOpen(false);
          form.reset();
        })
        .catch((err: unknown) => {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to create job',
            color: 'red',
          });
        });
    } else if (editingId) {
      const input: Record<string, unknown> = {
        name: values.name.trim(),
        code: values.code.trim(),
        status: values.status,
      };
      if (values.description.trim()) {
        input.description = values.description.trim();
      } else {
        input.description = '';
      }
      if (values.clientName.trim()) {
        input.clientName = values.clientName.trim();
      } else {
        input.clientName = '';
      }
      if (values.budgetAmount !== undefined && values.budgetAmount !== null) {
        input.budgetAmount = values.budgetAmount;
      }
      if (values.startDate) input.startDate = values.startDate;
      if (values.endDate) input.endDate = values.endDate;
      if (values.managerId.trim()) input.managerId = values.managerId.trim();
      if (values.departmentId.trim()) input.departmentId = values.departmentId.trim();

      updateMutation.update(editingId, input as any)
        .then(() => {
          notifications.show({
            title: 'Job Updated',
            message: 'A new version has been created (append-only).',
            color: 'green',
          });
          setFormOpen(false);
          form.reset();
          setEditingId(null);
        })
        .catch((err: unknown) => {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to update job',
            color: 'red',
          });
        });
    }
  });

  const handleDelete = () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    deleteMutation.delete(deleteTarget.id)
      .then(() => {
        notifications.show({
          title: 'Job Deactivated',
          message: 'Job soft-deleted (new inactive version created).',
          color: 'green',
        });
        setDeleteTarget(null);
        setDeleteReason('');
      })
      .catch((err: unknown) => {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to delete job',
          color: 'red',
        });
      });
  };

  const getPercentUsed = (row: JobRow): number => {
    if (!row.budgetAmount || row.budgetAmount <= 0) return 0;
    const actual = row.actualCost ?? 0;
    return Math.min(Math.round((actual / row.budgetAmount) * 100), 100);
  };

  const getProgressColor = (percent: number): string => {
    if (percent >= 90) return 'red';
    if (percent >= 75) return 'orange';
    if (percent >= 50) return 'yellow';
    return 'green';
  };

  const columns: Column<JobRow>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: 100,
      render: (row) => (
        <Text size="sm" fw={600} ff="monospace">
          {row.code}
        </Text>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <Text size="sm" fw={500}>
          {row.name}
        </Text>
      ),
    },
    {
      key: 'clientName',
      label: 'Client',
      sortable: true,
      width: 150,
      render: (row) => (
        <Text size="sm" c={row.clientName ? undefined : 'dimmed'}>
          {row.clientName || '-'}
        </Text>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 110,
      render: (row) => <StatusBadge status={row.status} colorMap={STATUS_COLOR_MAP} />,
    },
    {
      key: 'budgetAmount',
      label: 'Budget',
      sortable: true,
      width: 120,
      render: (row) =>
        row.budgetAmount != null ? (
          <CurrencyDisplay amount={row.budgetAmount} size="sm" />
        ) : (
          <Text size="sm" c="dimmed">-</Text>
        ),
    },
    {
      key: 'actualCost',
      label: 'Actual Cost',
      sortable: true,
      width: 120,
      render: (row) =>
        row.actualCost != null ? (
          <CurrencyDisplay amount={row.actualCost} size="sm" />
        ) : (
          <Text size="sm" c="dimmed">$0.00</Text>
        ),
    },
    {
      key: 'percentUsed',
      label: '% Used',
      width: 140,
      render: (row) => {
        const percent = getPercentUsed(row);
        if (!row.budgetAmount || row.budgetAmount <= 0) {
          return <Text size="sm" c="dimmed">No budget</Text>;
        }
        return (
          <Stack gap={2}>
            <Progress
              value={percent}
              color={getProgressColor(percent)}
              size="sm"
              radius="xl"
            />
            <Text size="xs" c="dimmed" ta="center">
              {percent}%
            </Text>
          </Stack>
        );
      },
    },
    {
      key: 'managerName',
      label: 'Manager',
      width: 130,
      render: (row) => (
        <Text size="sm" c={row.managerName ? undefined : 'dimmed'}>
          {row.managerName || '-'}
        </Text>
      ),
    },
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) =>
        row.version != null ? (
          <Group gap={4}>
            <IconHistory size={14} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              v{row.version}
            </Text>
          </Group>
        ) : null,
    },
  ];

  const isMutating = createMutation.loading || updateMutation.loading;

  return (
    <Stack gap="md">
      <PageHeader
        title="Job Costing"
        subtitle={data ? `${data.length} jobs` : 'Track project costs against budgets'}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openCreate}>
            New Job
          </Button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, code, or client..."
        filters={filters}
        onFilterChange={handleFilterChange}
        filterValues={filterValues}
      />

      {!isLoading && filteredData.length === 0 && !data?.length ? (
        <EmptyState
          icon={<IconBriefcase size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No Jobs Found"
          message="Create your first job to start tracking project costs against budgets."
          action={{ label: 'Create Job', onClick: openCreate }}
        />
      ) : (
        <DataTable<JobRow>
          columns={columns}
          data={(filteredData as JobRow[]) ?? []}
          loading={isLoading}
          pagination={
            data
              ? {
                  page,
                  pageSize: 25,
                  total: data.length,
                  onPageChange: setPage,
                }
              : undefined
          }
          getRowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/job-costing/${row.id}`)}
          emptyMessage="No jobs match your current filters."
          actions={(row) => {
            const isActive = row.isActive !== false;
            const canEdit = isActive && row.status !== 'CANCELLED';
            return (
              <Group gap={4}>
                {canEdit && (
                  <>
                    <Tooltip label="Edit (creates new version)">
                      <ActionIcon
                        variant="light"
                        size="sm"
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(row);
                        }}
                      >
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Deactivate (soft-delete)">
                      <ActionIcon
                        variant="light"
                        size="sm"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(row);
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                )}
              </Group>
            );
          }}
        />
      )}

      {/* -- Create / Edit Modal ------------------------------------------------ */}
      <Modal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          form.reset();
          setEditingId(null);
        }}
        title={formMode === 'create' ? 'Create Job' : 'Edit Job (new version)'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {formMode === 'edit' && (
              <Text size="xs" c="dimmed">
                Saving will create a new version. The previous version remains immutable in the
                append-only audit trail.
              </Text>
            )}

            <Group grow>
              <TextInput
                label="Job Name"
                placeholder="e.g. Mercy Hospital Wing B"
                required
                {...form.getInputProps('name')}
              />
              <TextInput
                label="Job Code"
                placeholder="e.g. MH-2025-001"
                required
                {...form.getInputProps('code')}
              />
            </Group>

            <Textarea
              label="Description"
              placeholder="Optional description of this job/project..."
              minRows={2}
              {...form.getInputProps('description')}
            />

            <Group grow>
              <TextInput
                label="Client Name"
                placeholder="e.g. Mercy Health System"
                {...form.getInputProps('clientName')}
              />
              <NumberInput
                label="Budget Amount"
                placeholder="0.00"
                prefix="$"
                decimalScale={2}
                fixedDecimalScale
                min={0}
                thousandSeparator=","
                {...form.getInputProps('budgetAmount')}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Start Date"
                type="date"
                {...form.getInputProps('startDate')}
              />
              <TextInput
                label="End Date"
                type="date"
                {...form.getInputProps('endDate')}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Manager ID"
                placeholder="Employee ID of the project manager"
                {...form.getInputProps('managerId')}
              />
              <TextInput
                label="Department ID"
                placeholder="Department for this job"
                {...form.getInputProps('departmentId')}
              />
            </Group>

            {formMode === 'edit' && (
              <Select
                label="Status"
                data={STATUS_OPTIONS}
                {...form.getInputProps('status')}
              />
            )}

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                {formMode === 'create'
                  ? 'Append-only: This job will be created as version 1 with ACTIVE status.'
                  : 'Append-only: Saving creates a new version. The previous version remains immutable in the audit trail.'}
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setFormOpen(false);
                  form.reset();
                  setEditingId(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isMutating}>
                {formMode === 'create' ? 'Create Job' : 'Save New Version'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* -- Delete Confirmation Modal ------------------------------------------ */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteReason('');
        }}
        title="Deactivate Job"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>
              {deleteTarget?.name}
            </Text>{' '}
            ({deleteTarget?.code}) by creating a new inactive version (append-only). The original
            record is preserved for audit.
          </Text>
          <Textarea
            label="Reason for deactivation"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteReason('');
              }}
            >
              Keep Job
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteMutation.loading}
              onClick={handleDelete}
            >
              Deactivate Job
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
